import type { ReframeConfig } from "app/js/src/domain/clipping-studio/clipping-studio-project";

type Canvas2dDestRect = { x: number; y: number; w: number; h: number };

/**
 * Draws one frame of reframed video onto a 2D canvas context.
 * Caller must ensure video.readyState >= 2 and ctx is a valid 2D context.
 * Implements fill, fit (black/white/blur), customBackground, and split50.
 */
export function drawReframeFrame(
  video: HTMLVideoElement,
  ctx: CanvasRenderingContext2D,
  outW: number,
  outH: number,
  config: ReframeConfig,
  dstRect2d?: Canvas2dDestRect,
): void {
  if (dstRect2d) {
    const { x, y, w, h } = dstRect2d;
    if (w <= 0 || h <= 0) return;
    ctx.save();
    ctx.translate(x, y);
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();
    drawReframeFrame(video, ctx, w, h, config);
    ctx.restore();
    return;
  }

  const vidW = video.videoWidth;
  const vidH = video.videoHeight;
  if (!vidW || !vidH) return;

  const bgColor = config.backgroundStyle === "white" ? "#fff" : "#000";
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, outW, outH);

  if (config.layout === "fit") {
    if (config.backgroundStyle === "blur") {
      ctx.save();
      ctx.filter = "blur(28px)";
      const fillScale = Math.max(outW / vidW, outH / vidH);
      const fillW = vidW * fillScale;
      const fillH = vidH * fillScale;
      const fillX = (outW - fillW) / 2;
      const fillY = (outH - fillH) / 2;
      ctx.drawImage(video, 0, 0, vidW, vidH, fillX, fillY, fillW, fillH);
      ctx.filter = "none";
      ctx.restore();
    }
    const scale = Math.min(outW / vidW, outH / vidH);
    const sw = vidW * scale;
    const sh = vidH * scale;
    const dx = (outW - sw) / 2;
    const dy = (outH - sh) / 2;
    ctx.drawImage(video, 0, 0, vidW, vidH, dx, dy, sw, sh);
    return;
  }

  if (config.layout === "fill") {
    const targetAspect = outW / outH;
    const sourceAspect = vidW / vidH;

    // Auto-fill: cover-crop the source to the output aspect (like object-fit: cover),
    // then scale the cropped rectangle to the full output surface (no stretching).
    const r =
      config.crop ??
      (() => {
        if (sourceAspect > targetAspect) {
          // Source is wider than target -> crop width, keep full height.
          const width = targetAspect / sourceAspect;
          return { x: (1 - width) / 2, y: 0, width, height: 1 };
        }
        // Source is taller/narrower than target -> crop height, keep full width.
        const height = sourceAspect / targetAspect;
        return { x: 0, y: (1 - height) / 2, width: 1, height };
      })();
    const sx = r.x * vidW;
    const sy = r.y * vidH;
    const sw = r.width * vidW;
    const sh = r.height * vidH;
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
    return;
  }

  if (config.layout === "customBackground" && config.crop) {
    const r = config.crop;
    if (config.backgroundStyle === "blur") {
      ctx.save();
      ctx.filter = "blur(28px)";
      const fillScale = Math.max(outW / vidW, outH / vidH);
      const fillW = vidW * fillScale;
      const fillH = vidH * fillScale;
      const fillX = (outW - fillW) / 2;
      const fillY = (outH - fillH) / 2;
      ctx.drawImage(video, 0, 0, vidW, vidH, fillX, fillY, fillW, fillH);
      ctx.filter = "none";
      ctx.restore();
    }
    const scale = Math.min(outW / (r.width * vidW), outH / (r.height * vidH));
    const sw = r.width * vidW * scale;
    const sh = r.height * vidH * scale;
    const dx = (outW - sw) / 2;
    const dy = (outH - sh) / 2;
    ctx.drawImage(
      video,
      r.x * vidW,
      r.y * vidH,
      r.width * vidW,
      r.height * vidH,
      dx,
      dy,
      sw,
      sh,
    );
    return;
  }

  if (config.layout === "split50" && config.topCrop && config.bottomCrop) {
    const top = config.swapped ? config.bottomCrop : config.topCrop;
    const bottom = config.swapped ? config.topCrop : config.bottomCrop;
    const halfH = outH / 2;
    ctx.drawImage(
      video,
      top.x * vidW,
      top.y * vidH,
      top.width * vidW,
      top.height * vidH,
      0,
      0,
      outW,
      halfH,
    );
    ctx.drawImage(
      video,
      bottom.x * vidW,
      bottom.y * vidH,
      bottom.width * vidW,
      bottom.height * vidH,
      0,
      halfH,
      outW,
      halfH,
    );
  }
}
